import { useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import type { CreateChatFormValues } from "@/features/chat/schema";

interface ChatFormProps {
  form: UseFormReturn<CreateChatFormValues>;
  titleMaxLength?: number;
  messageMaxLength?: number;
  disabled?: boolean;
  titleMode?: "optional" | "required" | "hidden";
  messageLabelKey?: string;
}

export const ChatForm: React.FC<ChatFormProps> = ({
  form,
  titleMaxLength = 30,
  messageMaxLength = 500,
  disabled = false,
  titleMode = "optional",
  messageLabelKey = "chat.sendMessage",
}) => {
  const { t } = useTranslation();
  const [isTitleOpen, setIsTitleOpen] = useState(false);

  const isTitleRequired = titleMode === "required";
  const isTitleOptional = titleMode === "optional";
  const isTitleVisible = isTitleRequired || isTitleOpen;

  const titleLength = form.watch("title")?.length ?? 0;

  return (
    <div className="space-y-1">
      <div className="text-sm font-semibold text-text-subtitle">
        {t(messageLabelKey)}
      </div>
      {isTitleOptional ? (
        <Button
          type="button"
          onClick={() => setIsTitleOpen((prev) => !prev)}
          variant="outline"
          disabled={disabled}
        >
          {t("chat.addTitleOptional")}
        </Button>
      ) : null}

      {isTitleVisible && titleMode !== "hidden" && (
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <div className="flex justify-between">
                <span className="text-xs text-muted-foreground">
                  {titleLength}/{titleMaxLength}
                </span>
              </div>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t("chat.titlePlaceholder")}
                  maxLength={titleMaxLength}
                  disabled={disabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <FormField
        control={form.control}
        name="message"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <Textarea
                {...field}
                placeholder={t("chat.messagePlaceholder")}
                className="min-h-32"
                maxLength={messageMaxLength}
                disabled={disabled}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
